/**
 * Artwork Upload Toggle
 *
 * Controls a "Blank (No Design) vs Upload Artwork" radio shown above the
 * file uploader on products with custom.artwork_upload_optional = true.
 *
 * Form-property mechanism:
 *   The hidden <input id="artwork-blank-property-input" name="properties[Artwork]"
 *   value="Blank — no design" form="<product_form_id>"> is rendered server-side
 *   inside the toggle snippet. It's bound to the product form via the HTML5
 *   `form` attribute (matches the canonical theme pattern in main-product.liquid
 *   line-item-property blocks). This script toggles the input's `disabled` state:
 *     - choice = "blank"  -> disabled=false -> input included in FormData -> cart
 *                            shows visible "Artwork: Blank — no design"
 *     - choice = "upload" -> disabled=true  -> input excluded from FormData. The
 *                            file-upload flow (add-to-cart-unified.js) creates
 *                            its own properties[Artwork] inside the form, no
 *                            conflict with our outside-the-form input.
 *
 *   When switching from upload back to blank (after an upload happened) we also
 *   strip any properties[Artwork] inputs the upload flow may have appended
 *   inside the form, so our re-enabled input is the sole source of truth.
 */
(() => {
  'use strict';

  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[ArtworkUploadToggle]', ...args);

  const BLANK_INPUT_ID = 'artwork-blank-property-input';
  const UPLOADER_CONTAINER_ID = 'custom-file-uploader-container';
  const ARTWORK_PROPERTY_NAME = 'properties[Artwork]';

  const getBlankInput = () => document.getElementById(BLANK_INPUT_ID);

  const getProductForm = (blankInput) => {
    if (!blankInput) return null;
    const formId = blankInput.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      if (form) return form;
    }
    return document.querySelector('form[action*="/cart/add"]');
  };

  const enableBlankProperty = () => {
    const input = getBlankInput();
    if (!input) {
      log('⚠️ Blank-property input not found in DOM');
      return;
    }
    input.disabled = false;
    log('🟣 Enabled cart property input (Blank — no design)');
  };

  const disableBlankProperty = () => {
    const input = getBlankInput();
    if (!input) return;
    input.disabled = true;
    log('🟣 Disabled cart property input (will be excluded from FormData)');
  };

  const stripUploadFlowArtworkInputs = (form) => {
    if (!form) return;
    const candidates = form.querySelectorAll(`input[name="${ARTWORK_PROPERTY_NAME}"]`);
    candidates.forEach((el) => {
      if (el.id === BLANK_INPUT_ID) return;
      el.remove();
      log('🧹 Removed stale upload-flow Artwork input from form');
    });
  };

  const clearUploadState = (form) => {
    const stateManager = window.PitchPrintStateManager;
    if (stateManager && typeof stateManager.clearUploadData === 'function') {
      try {
        stateManager.clearUploadData();
        log('🧹 Cleared StateManager upload data');
      } catch (err) {
        log('⚠️ Could not clear StateManager upload data:', err);
      }
    }

    if (window.FileUploader && typeof window.FileUploader.reset === 'function') {
      try {
        window.FileUploader.reset();
        log('🧹 Reset FileUploader UI');
      } catch (err) {
        log('⚠️ Could not reset FileUploader:', err);
      }
    }

    stripUploadFlowArtworkInputs(form);
  };

  const updateUploaderVisibility = (choice) => {
    const container = document.getElementById(UPLOADER_CONTAINER_ID);
    if (!container) {
      log('⚠️ Uploader container not found yet');
      return;
    }
    container.style.display = choice === 'upload' ? 'block' : 'none';
    log(`👁️ Uploader ${choice === 'upload' ? 'shown' : 'hidden'} (choice=${choice})`);
  };

  const initToggle = () => {
    const toggle = document.getElementById('artwork-upload-toggle');
    if (!toggle) {
      log('❌ Toggle element not found (product may not have artwork_upload_optional)');
      return;
    }

    const radioButtons = toggle.querySelectorAll('input[type="radio"][name="artwork_choice"]');
    if (radioButtons.length === 0) {
      log('❌ No radio buttons found in toggle');
      return;
    }

    const blankInput = getBlankInput();
    const form = getProductForm(blankInput);
    if (!blankInput) {
      log('⚠️ Blank-property input not rendered — cart property will not persist');
    }
    if (!form) {
      log('⚠️ Product form not found — upload-flow Artwork inputs cannot be cleaned up on switch');
    }

    const checkedRadio = Array.from(radioButtons).find((r) => r.checked);
    let currentChoice = checkedRadio ? checkedRadio.value : 'blank';

    updateUploaderVisibility(currentChoice);
    if (currentChoice === 'blank') {
      enableBlankProperty();
    } else {
      disableBlankProperty();
    }

    log(`✅ Initialized with choice=${currentChoice}`);

    radioButtons.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        if (!e.target.checked) return;
        const newChoice = e.target.value;
        if (newChoice === currentChoice) return;

        log(`🔄 Choice changed: ${currentChoice} → ${newChoice}`);
        currentChoice = newChoice;

        updateUploaderVisibility(newChoice);

        if (newChoice === 'blank') {
          clearUploadState(form);
          enableBlankProperty();
        } else {
          disableBlankProperty();
        }
      });
    });

    if (form) {
      form.addEventListener(
        'submit',
        () => {
          if (currentChoice === 'blank') {
            stripUploadFlowArtworkInputs(form);
            enableBlankProperty();
            log('🛡️ Submit guard: stripped competing Artwork inputs, ensured Blank value will reach cart');
          }
        },
        { capture: true }
      );
    }

    window.ArtworkUploadToggle = {
      getCurrentChoice: () => currentChoice,
      setChoice: (choice) => {
        const radio = toggle.querySelector(`input[value="${choice}"]`);
        if (radio) radio.click();
      }
    };

    log('✅ Artwork upload toggle ready');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initToggle);
  } else {
    initToggle();
  }

  log('📦 Artwork upload toggle script loaded');
})();
